/**
 * `/v1/person-fields`, `/v1/participation-roles`, `/v1/editions/:id/people` — lesingene og de ni
 * skrivingene (trinn 3).
 *
 * ================================================================================================
 * HVER SKRIVING FEIER HELE `qk.participations(orgId)`, OG DET ER MED VILJE.
 *
 * En deltakelse er ikke bare en rad: den er et forhold til avdelingstreets tellere, til
 * grupperingen «rolle», og til «Uten avdeling»-innboksen. Å plassere ett menneske endrer tre svar
 * i tre cacher. Samme resonnement som ressurslagets, og det er skrevet ned der også.
 *
 * DEN FEIER `qk.crew` OG `qk.volunteers` I TILLEGG. Personer-siden har tre faner fra før — vaktplan,
 * frivillige, akkreditering — og alle tre leser de samme menneskene fra sine egne kilder. En
 * deltakelse som skrives endrer ingen av de tabellene, men den endrer hva SKJERMEN skal vise om
 * personen, og de fire listene står ved siden av hverandre.
 *
 * ================================================================================================
 * FELTENE OG ROLLENE ER `CACHE.reference`, LISTA ER `CACHE.list`.
 *
 * Ordforrådet endrer seg i Innstillinger et par ganger i året; lista endrer seg hele dagen. Det er
 * den samme delingen ressurstypene og ressursene har.
 */
import {
  MAX_PAGE_SIZE,
  participationDepartmentSummarySchema,
  participationDtoSchema,
  participationListResponseSchema,
  participationRoleDtoSchema,
  participationRoleListResponseSchema,
  personFieldDtoSchema,
  personFieldListResponseSchema,
  personFieldUsageDtoSchema,
  type CreateParticipationRequest,
  type ParticipationCategory,
  type CreateParticipationRoleRequest,
  type CreatePersonFieldRequest,
  type ParticipationDepartmentSummary,
  type ParticipationDto,
  type ParticipationListResponse,
  type ParticipationRoleDto,
  type ParticipationRoleListResponse,
  type PersonFieldDto,
  type PersonFieldListResponse,
  type PersonFieldUsageDto,
  type UpdateParticipationRequest,
  type UpdateParticipationRoleRequest,
  type UpdatePersonFieldRequest,
} from '@tvitvi/shared';
import { queryOptions, useMutation, type UseMutationResult } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

/** Hva lista er filtrert på. Alt som er med her er med i cache-nøkkelen. */
export interface ParticipationFilters {
  departmentId?: string | undefined;
  participationRoleId?: string | undefined;
  q?: string | undefined;
  /** Ett menneske, slaatt opp paa kontakten — se kontrakten (#20). */
  contactId?: string | undefined;
  /** B1: kategorien som filter, og kategoriene søket treffer på brukerens språk (punkt 53). */
  category?: ParticipationCategory | undefined;
  qCategories?: readonly ParticipationCategory[] | undefined;
}

/** Hver skriving feier begge prefiksene — se filhodet for hva som ellers blir feil. */
const sweeps = (orgId: string) => [
  qk.participations(orgId),
  qk.crew(orgId),
  qk.volunteers(orgId),
  /* B1: Akkreditering leser den samme raden (kategori, rolle, pass). */
  qk.accreditations(orgId),
];

// --------------------------------------------------------------------------------- personfelt

/** @param includeArchived Innstillinger ber om dem, panelet gjør ikke — se ressurstypenes note. */
export function personFieldsQueryOptions(
  orgId: string,
  festivalId: string | null,
  includeArchived = false,
) {
  return queryOptions({
    queryKey: qk.personFieldList(orgId, festivalId, includeArchived),
    queryFn: ({ signal }): Promise<PersonFieldListResponse> =>
      apiRequest({
        path: '/person-fields',
        query: {
          ...(festivalId === null ? {} : { festivalId }),
          ...(includeArchived ? { includeArchived: 'true' } : {}),
        },
        schema: personFieldListResponseSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}

export function useCreatePersonField(
  orgId: string,
): UseMutationResult<PersonFieldDto, unknown, CreatePersonFieldRequest> {
  return useMutation({
    mutationFn: (body: CreatePersonFieldRequest) =>
      apiRequest({ path: '/person-fields', method: 'POST', body, schema: personFieldDtoSchema }),
    meta: { invalidates: sweeps(orgId) },
  });
}

export interface UpdatePersonFieldInput {
  personFieldId: string;
  body: UpdatePersonFieldRequest;
}

export function useUpdatePersonField(
  orgId: string,
): UseMutationResult<PersonFieldDto, unknown, UpdatePersonFieldInput> {
  return useMutation({
    mutationFn: ({ personFieldId, body }: UpdatePersonFieldInput) =>
      apiRequest({
        path: `/person-fields/${personFieldId}`,
        method: 'PATCH',
        body,
        schema: personFieldDtoSchema,
      }),
    meta: { invalidates: sweeps(orgId) },
  });
}

/** Hvor mange svar en sletting tar med seg (A5). Hentes når bekreftelsen åpnes. */
export function personFieldUsageQueryOptions(orgId: string, personFieldId: string) {
  return queryOptions({
    queryKey: qk.personFieldUsage(orgId, personFieldId),
    queryFn: ({ signal }): Promise<PersonFieldUsageDto> =>
      apiRequest({
        path: `/person-fields/${personFieldId}/usage`,
        schema: personFieldUsageDtoSchema,
        signal,
      }),
    staleTime: 0,
  });
}

/**
 * A5: slettingen tar svarene med seg, også i påmeldingsskjemaene og i ressurstypenes summering, så
 * de to feies i tillegg til deltakelsene.
 */
export function useRemovePersonField(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (personFieldId: string) =>
      apiRequest({ path: `/person-fields/${personFieldId}`, method: 'DELETE' }).then(
        () => undefined,
      ),
    meta: { invalidates: [...sweeps(orgId), qk.signupFormsAll(orgId), qk.resources(orgId)] },
  });
}

// -------------------------------------------------------------------------------------- roller

export function participationRolesQueryOptions(orgId: string, includeArchived = false) {
  return queryOptions({
    queryKey: qk.participationRoleList(orgId, includeArchived),
    queryFn: ({ signal }): Promise<ParticipationRoleListResponse> =>
      apiRequest({
        path: '/participation-roles',
        ...(includeArchived ? { query: { includeArchived: 'true' } } : {}),
        schema: participationRoleListResponseSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}

export function useCreateParticipationRole(
  orgId: string,
): UseMutationResult<ParticipationRoleDto, unknown, CreateParticipationRoleRequest> {
  return useMutation({
    mutationFn: (body: CreateParticipationRoleRequest) =>
      apiRequest({
        path: '/participation-roles',
        method: 'POST',
        body,
        schema: participationRoleDtoSchema,
      }),
    meta: { invalidates: sweeps(orgId) },
  });
}

export interface UpdateParticipationRoleInput {
  participationRoleId: string;
  body: UpdateParticipationRoleRequest;
}

export function useUpdateParticipationRole(
  orgId: string,
): UseMutationResult<ParticipationRoleDto, unknown, UpdateParticipationRoleInput> {
  return useMutation({
    mutationFn: ({ participationRoleId, body }: UpdateParticipationRoleInput) =>
      apiRequest({
        path: `/participation-roles/${participationRoleId}`,
        method: 'PATCH',
        body,
        schema: participationRoleDtoSchema,
      }),
    /* Akkrediteringslista slår opp rollenavnet i «Rolle, avdeling eller act» (sak 217.12). */
    meta: { invalidates: [...sweeps(orgId), qk.accreditations(orgId)] },
  });
}

export function useRemoveParticipationRole(
  orgId: string,
): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (participationRoleId: string) =>
      apiRequest({ path: `/participation-roles/${participationRoleId}`, method: 'DELETE' }).then(
        () => undefined,
      ),
    meta: { invalidates: sweeps(orgId) },
  });
}

// --------------------------------------------------------------------------------- deltakelser

export function participationsQueryOptions(
  orgId: string,
  editionId: string,
  filters: ParticipationFilters,
) {
  /*
   * EN TOM KATEGORILISTE ER INGEN KATEGORILISTE (QA13 bølge A, målt 10.10). Personer-fanen sender
   * alltid `qCategories: categoriesMatching(…)`, som er `[]` uten søk — og `[]` står i nøkkelen der
   * `undefined` forsvinner. Forespørselen var den samme, men nøkkelen en annen enn den
   * `route-prefetch.ts` varmet opp, så fanen hentet lista to ganger. Nøkkelen følger forespørselen.
   */
  const { qCategories, ...rest } = filters;
  const keyFilters =
    qCategories === undefined || qCategories.length === 0 ? rest : { ...rest, qCategories };
  return queryOptions({
    queryKey: qk.participationList(orgId, editionId, keyFilters),
    queryFn: ({ signal }): Promise<ParticipationListResponse> =>
      apiRequest({
        path: `/editions/${editionId}/people`,
        query: {
          editionId,
          ...(filters.departmentId === undefined ? {} : { departmentId: filters.departmentId }),
          ...(filters.participationRoleId === undefined
            ? {}
            : { participationRoleId: filters.participationRoleId }),
          ...(filters.q === undefined || filters.q === '' ? {} : { q: filters.q }),
          ...(filters.contactId === undefined ? {} : { contactId: filters.contactId }),
          ...(filters.category === undefined ? {} : { category: filters.category }),
          ...(filters.qCategories === undefined || filters.qCategories.length === 0
            ? {}
            : { qCategories: filters.qCategories.join(',') }),
          sort: 'name',
          direction: 'asc',
          limit: MAX_PAGE_SIZE,
        },
        schema: participationListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/** Tellerne i avdelingstreet. Faste for årgangen — avgjørelse 6A, lest på nytt for personer. */
export function participationSummaryQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.participationDepartmentSummary(orgId, editionId),
    queryFn: ({ signal }): Promise<ParticipationDepartmentSummary> =>
      apiRequest({
        path: '/participation-departments/summary',
        query: { editionId },
        schema: participationDepartmentSummarySchema,
        signal,
      }),
    ...CACHE.reference,
  });
}

export function useCreateParticipation(
  orgId: string,
): UseMutationResult<ParticipationDto, unknown, CreateParticipationRequest> {
  return useMutation({
    mutationFn: (body: CreateParticipationRequest) =>
      apiRequest({ path: '/participations', method: 'POST', body, schema: participationDtoSchema }),
    meta: { invalidates: sweeps(orgId) },
  });
}

export interface UpdateParticipationInput {
  participationId: string;
  body: UpdateParticipationRequest;
}

export function useUpdateParticipation(
  orgId: string,
): UseMutationResult<ParticipationDto, unknown, UpdateParticipationInput> {
  return useMutation({
    mutationFn: ({ participationId, body }: UpdateParticipationInput) =>
      apiRequest({
        path: `/participations/${participationId}`,
        method: 'PATCH',
        body,
        schema: participationDtoSchema,
      }),
    meta: { invalidates: sweeps(orgId) },
  });
}

/**
 * «FJERN FRA ÅRGANGEN» (sak 180, Atles svar 22. september).
 *
 * Ordet «slett» står ikke i denne funksjonen, og det er ikke pynt: `DELETE /participations/:id`
 * fjerner ÅRGANGENS EGEN FØRING om mennesket. Kildene blir stående — staben er fortsatt stab, den
 * frivillige har fortsatt en godkjent søknad — så personen kan komme tilbake i lista etterpå, uten
 * roller, felt og avdelinger. Det er tjenestens dokumenterte oppførsel, og det er derfor
 * bekreftelsen sier «fjern fra årgangen» framfor «slett».
 */
/** «Angre» på «Fjern fra årgangen» (sak 217.19): føringen tilbake med felt, roller og avdelinger. */
export function useRestoreParticipation(
  orgId: string,
): UseMutationResult<ParticipationDto, unknown, string> {
  return useMutation({
    mutationFn: (participationId: string) =>
      apiRequest({
        path: `/participations/${participationId}/restore`,
        method: 'POST',
        schema: participationDtoSchema,
      }),
    meta: { invalidates: sweeps(orgId) },
  });
}

export function useRemoveParticipation(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (participationId: string) =>
      apiRequest({ path: `/participations/${participationId}`, method: 'DELETE' }).then(
        () => undefined,
      ),
    meta: { invalidates: sweeps(orgId) },
  });
}
